import React from 'react';
import './styles/api_overview.scss';

const ApiOveriew = () => {
  const sectionHeaders = {
    'Overview': overview,
    'Authentication': authentication,
    "Content-Type": content_type,
    "Endpoint Documentation": endpoint_documentation,

    "Expandable Attributes": expandable,
    "Pagination": pagination,
    "Rate Limiting": rate_limiting,
    "Response Statuses": response_statuses,
    "Versioning": versioning
  }

  return (<div className={'api-overview'}>
    <h1>Finalsite Enrollment API Documentation</h1>
    <span className={'helper-text'}>Last Updated November 19, 2021</span>
    <p className={'spacing'}><span>This document provides a general overview of the concepts underlying the Finalsite Enrollment API and how to best use the API for success. For individual endpoint documentation,
      <a href={'/api/external/docs'}> click here</a></span></p>

    <ul>
      {Object.keys(sectionHeaders).map((header) => <li><a href={'#' + header.replace(/ /g, "_")}>{header}</a></li>)}
    </ul>

    {Object.keys(sectionHeaders).map((header) => <div><h2
      id={header.replace(/ /g, "_")}>{header}</h2>{sectionHeaders[header]()}</div>
    )}
  </div>)
}

const overview = () => (<div>
  <p>The Finalsite Enrollment API is built on RESTful principals and meant for retrieving information from a specific Finalsite Enrollment
    site.
    Each set of API credentials are generated per customer and correspond to a specific API user.
    API Credentials are created and provided by the customer support team to customers who have purchased the API.
    At this time, the Finalsite Enrollment API is read-only, meaning only GET requests are accepted and no data can be written to
    the system via the API.</p>
  <p> The API is only accessible through HTTPS.</p>
</div>)

const authentication = () => (<div>
    <p>The API uses <a href='https://jwt.io/introduction'>JSON Web Tokens</a> (JWTs) to authenticate requests.</p>
    <p>An API credential ID and API secret are required to create a valid JWT. The ID identifies an API credential
      belonging to an API user and is used as part of generating the token.
      The secret is used to sign the token.</p>
    <p>The JWT must be generated with the following claims and signed with the corresponding secret:</p>
    <pre className={'card card-body bg-light'}>
{`  {"sub": "api_credential_id",
  "name": "human readable name that identifies the calling program",
  "exp": "expiration time in seconds since epoch"}`}
    </pre>
    <h3>Expiration Time</h3>
    <p>This field specifies when the token will expire.</p>
    <p>
      <ol>
        <li>Value is a <a href={"https://en.wikipedia.org/wiki/Unix_time"}>Unix Epoch Timestamp</a> - The number of seconds that have elapsed since January 1st 1970, 12:00:00 AM UTC</li>
        <li>The expiration can be no more than sixty minutes in the future.</li>
      </ol>
    </p>
    <h3>Authorization Header</h3>
    <p>The generated token must then be included in the Authorization header as <code>Bearer token</code>, for example:
      <pre className={'card card-body bg-light'}>
        {`Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IlJvZ2VyIFdpbGNvIiwiaWF0IjoxNTE2MjM5MDIyfQ.2fA2T6_92g3xomqL0IjrW_6XMBafS9uU1LRGRaAUmKE`}
      </pre>
    </p>
  </div>
)

const content_type = () => (
  <div>
    <p>The API returns all responses as JSON.</p></div>
)

const expandable = () => (
  <div>
    <p>
      Some endpoints support optional attributes, which might include deeper information about related objects.
    </p>
    <p>
      Objects marked as x-expandable in endpoint documentation can be included or excluded using the <code>includes</code> and <code>excludes</code> request parameters.
    </p>
    <p>
      For example, the contacts endpoint includes information minimal information about each contact's relatives by default.
      A full <code>Contact</code> object for each relative can be optionally included by specifying the <code>?includes=contacts.relationships.contact</code>
      request parameter.
    </p>
  </div>
)

const pagination = () => (
  <div><p>Endpoints with pagination will return a maximum of 25 objects. When more than 25 objects are returned, the
    response will include a <code>meta</code> key containing a <code>next_cursor</code>, for example:</p>
    <pre
      className={'card card-body bg-light'}>{`{meta: {next_cursor: 'MjVkMjE3NDctM2MwYS0xMWVjLThlMGEtMDI0MmFjMTIwMDA1'}} `}</pre>
    <p>This cursor must be included as a query param in the next request with the key <code>cursor</code> in order to retrieve the next set of results. When there
      are no more results, the next_cursor will be returned as nil. </p></div>
)

const endpoint_documentation = () => (
  <div>
    <p>Documentation for how to interact with individual API endpoints and the schemas for the data returned by those endpoints can be accessed <a href={'/api/external/docs'}>here</a>.
      This information is programmatically kept up to date and reflects the latest state of the API and the latest version of specific endpoints.
    </p>
  </div>
)
const rate_limiting = () => (
  <div><p>Requests to the API are rate-limited to a rate of 15 requests per 10 seconds. Requests that exceed this limit
    will return a 429 response with a Retry-After header specifying how long to wait before the next request. </p></div>
)

const response_statuses = () => (
  <div>
    <p>The API uses the following standard HTTP response statuses.</p>
    <table className={'table'}>
      <thead>
      <th>Status Code</th>
      <th>Description</th>
      </thead>
      <tbody>
      <tr>
        <td>200 OK</td>
        <td>The request succeeded. The body of the response should be a json object with the requested data.</td>
      </tr>
      <tr>
        <td>401 Unauthorized</td>
        <td>The authorization data provided was not valid.</td>
      </tr>
      <tr>
        <td>404 Not Found</td>
        <td>The requested resource could not be found. You may be trying to access a record that does not exist, or you
          may have supplied an invalid URL.
        </td>
      </tr>
      <tr>
        <td>429 Too Many Requests</td>
        <td>You will see this response if you've exceeded the rate limit. This response should include a Retry-After
          (see <a href={'#Rate_Limiting'}>Rate Limiting</a>).
        </td>
      </tr>
      <tr>
        <td>500 Internal Server Error</td>
        <td>An unexpected error has occurred. This response should include a JSON error object describing what went
          wrong.
        </td>
      </tr>
      </tbody>
    </table>
  </div>
)

const versioning = () => (
  <div>
    <p>The API uses a single major version number. This version number is incremented whenever breaking changes are
      introduced. This version is an integer, specified with the header <code>X-Api-Version</code>. For example:
      <pre className={'card card-body bg-light inline-block'}>{`X-Api-Version: 1`}</pre>
      It is always recommended to use the latest version of an endpoint available. The version <span className={'bold'}>will not</span> be
      updated when the following occurs:</p>
    <ol>
      <li>Non-breaking, backwards compatible change are introduced</li>
      <li>New attributes are added to a result</li>
      <li>Validations are added to prevent bad data from entering the system</li>
    </ol>
  </div>
)

export default ApiOveriew;
